/**
 * VehicleSlideshow
 * -----------------
 * Öffentliche Landing-Slideshow für Fahrzeug-Aufbereitungen. Rendert je
 * nach `style` unterschiedlich:
 *   - `carousel`: horizontal scrollende Slides, Autoplay optional
 *   - `grid`: statisches 3-spaltiges Raster
 *   - `ticker`: endloses Laufband (CSS-Animation)
 *
 * SEO:
 *   - Jedes Bild trägt einen individuellen `alt`-Text (Pflichtfeld im
 *     Editor).
 *   - Ein zusätzlicher JSON-LD `ImageObject`-Graph wird ausgegeben, damit
 *     Google Images die Bilder mit Titel und Kontext indexieren kann.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Images } from 'lucide-react';
import { resolveImageUrl as AbsoluteUrl } from '../../lib/resolveImageUrl';

const AUTOPLAY_MS = 8500;

// Image licensing / attribution defaults required by Google's Image Rights
// structured-data guidelines (license, creditText, acquireLicensePage,
// copyrightNotice). Centralised so we can enrich every ImageObject we emit.
const IMAGE_RIGHTS = {
  license: 'https://www.chrobe.de/impressum',
  acquireLicensePage: 'https://www.chrobe.de/kontakt',
  creditText: 'CHROBE One World',
  copyrightNotice: `© ${new Date().getFullYear()} CHROBE One World`,
  copyrightHolder: { '@type': 'Organization', name: 'CHROBE One World' },
};

const injectJsonLd = (items) => {
  if (!items?.length || typeof window === 'undefined') return null;
  const origin = window.location.origin;
  const graph = [];
  for (const it of items) {
    if (!it.image_url) continue;
    const push = (url, title, description) => {
      graph.push({
        '@type': 'ImageObject',
        contentUrl: url.startsWith('http') ? url : `${origin}${url}`,
        name: title || 'Fahrzeug-Aufbereitung',
        description: description || '',
        creator: { '@type': 'Organization', name: 'CHROBE One World' },
        ...IMAGE_RIGHTS,
      });
    };
    push(it.image_url, it.title || it.alt_text, it.description || it.alt_text);
    for (const sub of (it.images || [])) {
      if (sub?.url) push(sub.url, it.title || sub.alt_text, sub.alt_text || it.description);
    }
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: 'CHROBE One World — Fahrzeug-Aufbereitungen',
    image: graph,
  };
};

export const VehicleSlideshow = ({ config }) => {
  const cfg = config || {};
  const items = (cfg.items || []).filter(it => it?.image_url);
  const [idx, setIdx] = useState(0);
  const [lightboxItem, setLightboxItem] = useState(null); // { images:[{url,alt}], startIdx }
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const trackRef = useRef(null);

  const jsonLd = useMemo(() => injectJsonLd(items), [items]);

  // Autoplay nur für Karussell
  useEffect(() => {
    if (!['carousel', 'selector'].includes(cfg.style) || !cfg.autoplay || items.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [cfg.style, cfg.autoplay, items.length]);

  // Ganze Bilder-Liste eines Items zusammenstellen: image_url + evtl. images[]
  const collectImages = (it) => {
    const arr = [{ url: it.image_url, alt: it.alt_text || it.title || '', title: it.title || '' }];
    for (const sub of (it.images || [])) {
      if (sub?.url) arr.push({ url: sub.url, alt: sub.alt_text || it.alt_text || '', title: '' });
    }
    return arr;
  };

  const openLightbox = (it, startIdx = 0) => {
    const all = collectImages(it);
    if (all.length === 0) return;
    setLightboxItem({ ...it, allImages: all });
    setLightboxIdx(startIdx);
  };
  const closeLightbox = () => setLightboxItem(null);

  // Lightbox Keyboard-Navigation
  useEffect(() => {
    if (!lightboxItem) return;
    const total = lightboxItem.allImages.length;
    const handler = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % total);
      if (e.key === 'ArrowLeft') setLightboxIdx(i => (i - 1 + total) % total);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxItem]);

  if (!cfg.enabled || items.length === 0) return null;

  const heading = cfg.title || 'Aus unserer Werkstatt';
  const subtitle = cfg.subtitle || 'Einblicke in aktuelle Aufbereitungen';

  const goPrev = () => setIdx(i => (i - 1 + items.length) % items.length);
  const goNext = () => setIdx(i => (i + 1) % items.length);

  // Renderer für einen Item-Content im Karussell (mit Projekt-Badge)
  const projectBadge = (it) => {
    const count = (it.images || []).filter(s => s?.url).length;
    if (count === 0) return null;
    return (
      <span className="absolute top-4 right-4 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-xs font-medium border border-white/10">
        <Images className="w-3 h-3" />
        {count + 1} Bilder
      </span>
    );
  };

  return (
    <section
      className="py-20 sm:py-24 bg-[#0a0909] border-t border-b border-[#605e5e]/10"
      data-testid="vehicle-slideshow"
    >
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-[#0094ff] text-xs font-semibold tracking-[0.3em] uppercase">
            {subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3">
            {heading}
          </h2>
        </div>

        {/* ============ CAROUSEL ============ */}
        {cfg.style === 'carousel' && (
          <div className="relative">
            <div className="overflow-hidden rounded-2xl bg-[#161313] border border-[#605e5e]/20">
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative aspect-[16/9] cursor-pointer group"
                onClick={() => openLightbox(items[idx], 0)}
                data-testid="slideshow-open-lightbox"
              >
                <img
                  src={AbsoluteUrl(items[idx].image_url)}
                  alt={items[idx].alt_text || items[idx].title || 'Fahrzeug-Aufbereitung'}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
                {projectBadge(items[idx])}
                {(items[idx].title || items[idx].description) && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent p-6 sm:p-8">
                    {items[idx].title && (
                      <h3 className="text-white text-xl sm:text-2xl font-semibold">{items[idx].title}</h3>
                    )}
                    {items[idx].description && (
                      <p className="text-[#e5e5e5] text-sm mt-1 max-w-2xl">{items[idx].description}</p>
                    )}
                  </div>
                )}
              </motion.div>
            </div>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-2 sm:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  aria-label="Vorheriges Bild"
                  data-testid="slideshow-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-2 sm:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  aria-label="Nächstes Bild"
                  data-testid="slideshow-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="flex justify-center gap-1.5 mt-4">
                  {items.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIdx(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-[#0094ff] w-6' : 'bg-[#605e5e]'}`}
                      aria-label={`Zu Bild ${i + 1} springen`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ AUSWAHL (große Ansicht + Liste rechts) ============ */}
        {cfg.style === 'selector' && (
          <div className="grid lg:grid-cols-[1fr_320px] gap-4" data-testid="slideshow-selector">
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-[#161313] border border-[#605e5e]/20 cursor-pointer group"
              onClick={() => openLightbox(items[idx], 0)}
            >
              <img
                src={AbsoluteUrl(items[idx].image_url)}
                alt={items[idx].alt_text || items[idx].title || 'Fahrzeug-Aufbereitung'}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {projectBadge(items[idx])}
              {(items[idx].title || items[idx].description) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 sm:p-7">
                  {items[idx].title && <h3 className="text-white text-xl sm:text-2xl font-semibold">{items[idx].title}</h3>}
                  {items[idx].description && <p className="text-[#e5e5e5] text-sm mt-1 max-w-xl line-clamp-2">{items[idx].description}</p>}
                </div>
              )}
            </motion.div>
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto lg:max-h-[calc((100vw-2rem)*0.4)] xl:max-h-[470px] pb-1" role="listbox" aria-label="Fahrzeug auswählen">
              {items.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  role="option"
                  aria-selected={i === idx}
                  onClick={() => setIdx(i)}
                  className={`flex items-center gap-3 shrink-0 w-56 lg:w-auto text-left rounded-xl p-2 border transition-colors ${i === idx ? 'bg-[#0094ff]/10 border-[#0094ff]/50' : 'bg-[#161313] border-[#605e5e]/20 hover:border-[#605e5e]/60'}`}
                  data-testid={`slideshow-select-${i}`}
                >
                  <img
                    src={AbsoluteUrl(it.image_url)}
                    alt=""
                    className="w-20 h-14 object-cover rounded-lg shrink-0"
                    loading="lazy"
                  />
                  <span className="min-w-0">
                    <span className={`block text-sm font-medium truncate ${i === idx ? 'text-white' : 'text-[#d4d4d8]'}`}>{it.title || `Fahrzeug ${i + 1}`}</span>
                    {it.description && <span className="block text-xs text-[#a1a1aa] truncate">{it.description}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ============ POSTCARD RACK (3D Rotation) ============ */}
        {cfg.style === 'postcard' && (
          <div className="postcard-rack-wrap">
            <div
              className="postcard-rack"
              style={{ animationDuration: `${cfg.rotation_seconds || 32}s` }}
            >
              {items.map((it, i) => {
                const angle = (360 / items.length) * i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => openLightbox(it, 0)}
                    className="postcard-card"
                    style={{
                      transform: `rotateY(${angle}deg) translateZ(360px)`,
                    }}
                    aria-label={it.title || 'Fahrzeug ansehen'}
                    data-testid={`postcard-item-${i}`}
                  >
                    <div className="postcard-card-inner">
                      <div className="postcard-img-wrap">
                        <img
                          src={AbsoluteUrl(it.image_url)}
                          alt={it.alt_text || it.title || 'Fahrzeug-Aufbereitung'}
                          loading={i < 3 ? 'eager' : 'lazy'}
                        />
                        {projectBadge(it)}
                      </div>
                      {it.title && (
                        <div className="postcard-title">{it.title}</div>
                      )}
                    </div>
                    <div className="postcard-reflection" aria-hidden="true">
                      <img src={AbsoluteUrl(it.image_url)} alt="" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ============ GRID ============ */}
        {cfg.style === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it, i) => (
              <figure
                key={i}
                onClick={() => openLightbox(it, 0)}
                className="group rounded-xl overflow-hidden bg-[#161313] border border-[#605e5e]/20 cursor-pointer relative"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={AbsoluteUrl(it.image_url)}
                    alt={it.alt_text || it.title || 'Fahrzeug-Aufbereitung'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading={i < 3 ? 'eager' : 'lazy'}
                  />
                  {projectBadge(it)}
                </div>
                {(it.title || it.description) && (
                  <figcaption className="p-4">
                    {it.title && <h3 className="text-white font-semibold text-base">{it.title}</h3>}
                    {it.description && (
                      <p className="text-[#a1a1aa] text-sm mt-1 line-clamp-3">{it.description}</p>
                    )}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        {/* ============ TICKER ============ */}
        {cfg.style === 'ticker' && (
          <div className="relative overflow-hidden rounded-2xl">
            <div
              ref={trackRef}
              className="flex gap-4 animate-marquee"
              style={{ animationDuration: `${Math.max(20, items.length * 6)}s` }}
            >
              {[...items, ...items].map((it, i) => (
                <figure
                  key={i}
                  onClick={() => openLightbox(it, 0)}
                  className="flex-shrink-0 w-72 rounded-xl overflow-hidden bg-[#161313] border border-[#605e5e]/20 cursor-pointer relative"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={AbsoluteUrl(it.image_url)}
                      alt={it.alt_text || it.title || 'Fahrzeug-Aufbereitung'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {projectBadge(it)}
                  </div>
                  {it.title && (
                    <figcaption className="p-3">
                      <h3 className="text-white text-sm font-medium truncate">{it.title}</h3>
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============ LIGHTBOX ============ */}
      {lightboxItem && (
        <div
          className="fixed inset-0 z-overlay-nav bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
            aria-label="Galerie schließen"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div
            className="max-w-6xl w-full max-h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex-1 flex items-center justify-center min-h-0">
              <img
                src={AbsoluteUrl(lightboxItem.allImages[lightboxIdx].url)}
                alt={lightboxItem.allImages[lightboxIdx].alt || lightboxItem.title || 'Fahrzeug-Detail'}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
              {lightboxItem.allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxIdx(i => (i - 1 + lightboxItem.allImages.length) % lightboxItem.allImages.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
                    aria-label="Vorheriges Bild"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setLightboxIdx(i => (i + 1) % lightboxItem.allImages.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
                    aria-label="Nächstes Bild"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            {/* Info + Thumbnails */}
            <div className="mt-4 px-2">
              {lightboxItem.title && (
                <h3 className="text-white text-lg font-semibold text-center">{lightboxItem.title}</h3>
              )}
              {lightboxItem.description && (
                <p className="text-[#a1a1aa] text-sm text-center mt-1 max-w-3xl mx-auto">{lightboxItem.description}</p>
              )}
              <div className="text-center text-[#a1a1aa] text-xs mt-2">
                {lightboxIdx + 1} / {lightboxItem.allImages.length}
              </div>
              {lightboxItem.allImages.length > 1 && (
                <div className="mt-3 flex gap-2 justify-center flex-wrap max-h-24 overflow-y-auto">
                  {lightboxItem.allImages.map((thumb, ti) => (
                    <button
                      key={ti}
                      type="button"
                      onClick={() => setLightboxIdx(ti)}
                      className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${ti === lightboxIdx ? 'border-[#0094ff]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    >
                      <img
                        src={AbsoluteUrl(thumb.url)}
                        alt={thumb.alt || `Bild ${ti + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS für Ticker + Postkartenständer-Animation */}
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-marquee { animation: marquee linear infinite; will-change: transform; }
        .animate-marquee:hover { animation-play-state: paused; }

        @keyframes postcard-spin {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(-360deg); }
        }
        .postcard-rack-wrap {
          perspective: 1200px;
          width: 100%;
          height: 460px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible;
        }
        .postcard-rack {
          position: relative;
          width: 260px;
          height: 340px;
          transform-style: preserve-3d;
          animation: postcard-spin linear infinite;
          will-change: transform;
        }
        .postcard-rack:hover { animation-play-state: paused; }
        .postcard-card {
          position: absolute;
          top: 0;
          left: 0;
          width: 260px;
          height: 340px;
          border-radius: 14px;
          overflow: visible;
          background: transparent;
          cursor: pointer;
          padding: 0;
          text-align: left;
          backface-visibility: hidden;
        }
        .postcard-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 14px;
          overflow: hidden;
          background: #161313;
          border: 1px solid rgba(96,94,94,0.25);
          box-shadow: 0 12px 32px rgba(0,0,0,0.45);
          transition: box-shadow 0.3s;
        }
        .postcard-card:hover .postcard-card-inner {
          box-shadow: 0 0 0 2px #0094ff, 0 12px 32px rgba(0,148,255,0.35);
        }
        .postcard-reflection {
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          height: 55%;
          margin-top: 4px;
          border-radius: 14px;
          overflow: hidden;
          transform: scaleY(-1);
          opacity: 0.35;
          filter: blur(1px);
          -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.55), transparent 90%);
          mask-image: linear-gradient(to top, rgba(0,0,0,0.55), transparent 90%);
          pointer-events: none;
        }
        .postcard-reflection img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top;
        }
        .postcard-img-wrap {
          position: relative;
          width: 100%;
          height: 260px;
          overflow: hidden;
        }
        .postcard-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .postcard-title {
          padding: 12px 14px;
          color: #fff;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (max-width: 640px) {
          .postcard-rack-wrap { height: 360px; }
          .postcard-rack { width: 200px; height: 260px; }
          .postcard-card { width: 200px; height: 260px; }
          .postcard-img-wrap { height: 200px; }
        }
      `}</style>
    </section>
  );
};

export default VehicleSlideshow;
